/**
 * Page-level actions in the page header (spec §8.22, M19): **Restrict writing…**, which edits the
 * page's ACL (§10.2 — it restricts *writing*, never reading), and **Delete page…**, which soft-deletes
 * it into the workspace's trash (§13.9). Both need write access to the page; only an owner may set an
 * ACL, so the menu says so rather than failing at the server.
 */
import { useNavigate } from "@tanstack/react-router";
import { Link2, MoreHorizontal, ShieldBan, Trash2 } from "lucide-react";
import { useState } from "react";
import { ApiError } from "../api/client";
import { useAcl, useDeletePage, useMembers, useSetAcl, useWorkspace } from "../api/hooks";
import { Button } from "../ui/Button";
import { usePopover } from "../ui/usePopover";
import { Rich, useT } from "../i18n";
import { roleKey } from "../i18n/roles";

export function PageActions({ workspaceId, docId, title, readOnly, onLinkPage }: { workspaceId: string; docId: string; title: string; readOnly: boolean; onLinkPage?: () => void }) {
  // dismissal is the shared one of §8.6 item 5: hover tolerance, a press outside, Escape
  const { open, toggle, close, buttonRef, panelRef } = usePopover();
  const t = useT();
  const [dialog, setDialog] = useState<null | "acl" | "delete">(null);
  const ws = useWorkspace(workspaceId);
  const owner = ws.data?.my_role === "owner";
  return (
    <span className="relative flex items-center" data-testid="page-actions">
      <button
        ref={buttonRef}
        className="rounded-sm px-1.5 py-1 text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
        aria-label={t("pageActions.menu")}
        aria-haspopup="menu"
        aria-expanded={open}
        title={t("pageActions.menu")}
        data-testid="page-actions-button"
        onClick={toggle}
      >
        <MoreHorizontal size={16} />
      </button>
      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 top-8 z-30 w-56 rounded-md border border-neutral-200 bg-[var(--surface)] p-1 text-sm shadow-[var(--shadow-md)] dark:border-neutral-700"
          data-testid="page-actions-menu"
        >
          {onLinkPage && (
            <MenuItem
              icon={<Link2 size={14} />}
              label={t("pageActions.linkPage")}
              testId="page-link-page"
              onClick={() => {
                close();
                onLinkPage();
              }}
            />
          )}
          <MenuItem
            icon={<ShieldBan size={14} />}
            label={t("pageActions.restrict")}
            disabled={!owner}
            title={owner ? undefined : t("pageActions.restrict.ownerOnly")}
            testId="page-restrict"
            onClick={() => {
              close();
              setDialog("acl");
            }}
          />
          <MenuItem
            icon={<Trash2 size={14} />}
            label={t("pageActions.delete")}
            disabled={readOnly}
            title={readOnly ? t("pageActions.delete.readOnly") : undefined}
            testId="page-delete"
            onClick={() => {
              close();
              setDialog("delete");
            }}
          />
        </div>
      )}
      {dialog === "acl" && <RestrictDialog workspaceId={workspaceId} docId={docId} title={title} onClose={() => setDialog(null)} />}
      {dialog === "delete" && <DeleteDialog workspaceId={workspaceId} docId={docId} title={title} onClose={() => setDialog(null)} />}
    </span>
  );
}

function MenuItem({ icon, label, disabled, title, testId, onClick }: { icon: React.ReactNode; label: string; disabled?: boolean; title?: string; testId: string; onClick(): void }) {
  return (
    <button
      className="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left hover:bg-neutral-100 disabled:opacity-40 dark:hover:bg-neutral-800"
      disabled={disabled}
      title={title}
      data-testid={testId}
      onClick={onClick}
    >
      {icon} {label}
    </button>
  );
}

function Shell({ label, children, onClose }: { label: string; children: React.ReactNode; onClose(): void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-[rgba(28,27,26,0.36)] p-8" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-lg border border-neutral-200 bg-[var(--surface)] p-5 text-sm shadow-[var(--shadow-lg)] dark:border-neutral-700"
        role="dialog"
        aria-label={label}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/** Who may write this page. Read access stays workspace-wide (spec §10.2). */
function RestrictDialog({ workspaceId, docId, title, onClose }: { workspaceId: string; docId: string; title: string; onClose(): void }) {
  const t = useT();
  const members = useMembers(workspaceId);
  const acl = useAcl(docId);
  const save = useSetAcl(docId);
  const [error, setError] = useState<string | null>(null);
  const restricted = new Set(acl.data?.restricted_user_ids ?? []);

  const toggle = (userId: string, on: boolean) => {
    const next = new Set(restricted);
    if (on) next.add(userId);
    else next.delete(userId);
    setError(null);
    save.mutate([...next], { onError: (e) => setError(e instanceof ApiError ? e.detail : String(e)) });
  };

  return (
    <Shell label={t("restrict.dialog")} onClose={onClose}>
      <h2 className="mb-1 text-[17px] font-semibold">{t("restrict.heading", { title: title || t("page.untitled") })}</h2>
      <p className="mb-3 text-neutral-500">
        <Rich text={t("restrict.body")} />
      </p>
      {error && (
        <p className="mb-2 rounded-sm bg-err-tint px-3 py-2 text-err" role="alert" data-testid="restrict-error">
          {error}
        </p>
      )}
      <ul className="mb-4 divide-y divide-neutral-200 dark:divide-neutral-800" data-testid="restrict-list">
        {(members.data ?? [])
          .filter((m) => m.role !== "viewer")
          .map((m) => (
            <li key={m.user.id} className="flex items-center gap-2 py-2" data-username={m.user.username}>
              <span className="grow truncate">
                {m.user.display_name || m.user.username} <span className="text-xs text-neutral-500">{t(roleKey(m.role))}</span>
              </span>
              <label className="flex items-center gap-1 text-xs text-neutral-500">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--color-accent)]"
                  checked={restricted.has(m.user.id)}
                  data-testid="restrict-toggle"
                  onChange={(e) => toggle(m.user.id, e.target.checked)}
                />
                {t("ws.readOnly")}
              </label>
            </li>
          ))}
        {members.data?.every((m) => m.role === "viewer") && <li className="py-2 text-neutral-500">{t("restrict.onlyViewers")}</li>}
      </ul>
      <div className="flex justify-end">
        <Button variant="ghost" onClick={onClose} data-testid="restrict-close">
          {t("common.done")}
        </Button>
      </div>
    </Shell>
  );
}

function DeleteDialog({ workspaceId, docId, title, onClose }: { workspaceId: string; docId: string; title: string; onClose(): void }) {
  const t = useT();
  const remove = useDeletePage(workspaceId);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  return (
    <Shell label={t("delete.dialog")} onClose={onClose}>
      <h2 className="mb-1 text-[17px] font-semibold">{t("delete.heading", { title: title || t("page.untitled") })}</h2>
      <p className="mb-4 text-neutral-500">{t("delete.body")}</p>
      {error && (
        <p className="mb-2 rounded-sm bg-err-tint px-3 py-2 text-err" role="alert" data-testid="delete-error">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} data-testid="delete-cancel">
          {t("common.cancel")}
        </Button>
        <Button
          variant="danger"
          data-testid="delete-confirm"
          onClick={() =>
            remove.mutate(docId, {
              onSuccess: () => void navigate({ to: "/w/$workspaceId", params: { workspaceId } }),
              onError: (e) => setError(e instanceof ApiError ? e.detail : String(e)),
            })
          }
        >
          {t("delete.dialog")}
        </Button>
      </div>
    </Shell>
  );
}
